<template>
  <div ref="frameContainer" class="frame-container">
    <img :src="frameImageSrc" alt="Frame" class="frame-image" @load="updateDimensions" />
    <div class="content-window" :style="contentWindowStyle">
      <transition-group name="fade" tag="div">
        <img
          v-if="prevImage"
          :key="prevImage"
          :src="prevImage"
          class="content-image fading"
          :style="contentImageStyle"
          alt="Scrolling Content"
        />
        <img
          ref="contentImage"
          :key="currentImage"
          :src="currentImage"
          class="content-image"
          :style="contentImageStyle"
          @load="updateDimensions"
          alt="Scrolling Content"
        />
      </transition-group>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, onUnmounted, computed, reactive, watch } from 'vue';

export default {
  props: {
    frameImageSrc: {
      type: String,
      required: true
    },
    contentImageSrc: {
      type: String,
      required: true
    },
    // Define the area within the frame where the content should show (relative to frame dimensions)
    // Example: { top: 0.1, left: 0.1, width: 0.8, height: 0.8 } means 10% margin on all sides
    contentAreaRatio: {
      type: Object,
      default: () => ({ left: 0.2, width: 0.6, height: 1 }) // Adjust as needed for laptop-picture.png
    },
    scrollTriggerOffsetTop: { // Pixels above the component top to start the scroll effect
      type: Number,
      default: 200 
    },
    scrollTriggerOffsetBottom: { // Pixels below the component bottom to end the scroll effect
      type: Number,
      default: 200
    },
    scrollAnchors: {
      type: Array,
      default: () => []
    },
    anchorProgresses: {
      type: Array,
      default: () => []
    }
  },
  setup(props) {
    // Use toRefs to keep props reactive if destructured or used directly in computed/watch
    // const { scrollTriggerOffsetTop, scrollTriggerOffsetBottom, contentAreaRatio } = toRefs(props);

    const frameContainer = ref(null);
    const contentImage = ref(null);
    const frameDimensions = reactive({ width: 0, height: 0 });
    const contentDimensions = reactive({ width: 0, height: 0 });
    const contentScrollY = ref(0); // How much the content image is scrolled (negative value)
    const currentImage = ref(props.contentImageSrc);
    const prevImage = ref(null);

    const updateDimensions = () => {
      if (frameContainer.value && contentImage.value) {
        const frameRect = frameContainer.value.getBoundingClientRect();
        frameDimensions.width = frameRect.width;
        frameDimensions.height = frameRect.height;
        // Use naturalWidth/Height for intrinsic dimensions if needed, but clientWidth/Height for rendered size
        contentDimensions.width = contentImage.value.clientWidth; 
        contentDimensions.height = contentImage.value.clientHeight;
        // console.log('Dimensions Updated:', frameDimensions, contentDimensions);
      }
    };

    const scrollAnchors = props.scrollAnchors || [];

    function getAnchorPositions() {
      return scrollAnchors.map(anchor => {
        const el = typeof anchor === 'string' ? document.getElementById(anchor) : anchor;
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return rect.top + rect.height / 2 - window.innerHeight / 2; // distance from center
      });
    }

    const handleScroll = () => {
      if (!frameContainer.value || !contentImage.value || !contentDimensions.height) return;


      const positions = getAnchorPositions();
      // Find the anchor closest to the center
      let minDist = Infinity;
      let closestIdx = 0;
      positions.forEach((dist, idx) => {
        if (dist !== null && Math.abs(dist) < Math.abs(minDist)) {
          minDist = dist;
          closestIdx = idx;
        }
      });
      // Map closestIdx to a scroll position in the screenshot
      let anchorProgress;
      if (props.anchorProgresses && props.anchorProgresses.length === scrollAnchors.length) {
        anchorProgress = props.anchorProgresses[closestIdx];
      } else {
        anchorProgress = scrollAnchors.length > 1 ? closestIdx / (scrollAnchors.length - 1) : 0;
      }

      // Calculate the maximum scroll distance for the content image
      const maxContentScroll = contentWindowStyle.value.heightPx 
                               ? contentDimensions.height - contentWindowStyle.value.heightPx
                               : 0;

      // Set the scroll position of the screenshot
      if (maxContentScroll > 0) {
        contentScrollY.value = -anchorProgress * maxContentScroll;
      } else {
        contentScrollY.value = 0;
      }
      // console.log(`Scroll Progress: ${progress.toFixed(2)}, Content Y: ${contentScrollY.value.toFixed(2)}`);
    };

    onMounted(() => {
      window.addEventListener('scroll', handleScroll);
      window.addEventListener('resize', updateDimensions);
      // Use requestAnimationFrame to ensure initial dimensions are available after render
      requestAnimationFrame(updateDimensions); 
      // Might need a slight delay or MutationObserver if images load slowly
      setTimeout(updateDimensions, 100); // Small delay fallback
      handleScroll(); // Initial check
    });

    onUnmounted(() => {
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateDimensions);
    });

    // Calculate the absolute pixel styles for the content window based on frame dimensions and ratio
    const contentWindowStyle = computed(() => {
      if (!frameDimensions.width || !frameDimensions.height) {
        return { top: '0px', left: '0px', width: '0px', height: '0px', heightPx: 0 };
      }
      const topPx = frameDimensions.height * props.contentAreaRatio.top;
      const leftPx = frameDimensions.width * props.contentAreaRatio.left;
      const widthPx = frameDimensions.width * props.contentAreaRatio.width;
      const heightPx = frameDimensions.height * props.contentAreaRatio.height;
      return {
        top: `${topPx}px`,
        left: `${leftPx}px`,
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        heightPx: heightPx // Store raw height for calculations
      };
    });

    // Calculate the style for the scrolling content image
    const contentImageStyle = computed(() => ({
      transform: `translateY(${contentScrollY.value}px)`,
      width: '100%', // Make content image fill the width of the window
      height: 'auto', // Maintain aspect ratio
      position: 'absolute',
      top: '0',
      left: '0',
    }));

    watch(
      () => props.contentImageSrc,
      (newVal, oldVal) => {
        if (newVal !== oldVal) {
          prevImage.value = oldVal;
          currentImage.value = newVal;
          setTimeout(() => {
            prevImage.value = null;
          }, 400); // match the CSS transition duration
        }
      }
    );

    // Return everything needed in the template
    return {
      frameContainer,
      contentImage,
      updateDimensions,
      contentWindowStyle,
      contentImageStyle,
      currentImage,
      prevImage
    };
  }
}
</script>

<style scoped>
.frame-container {
  position: relative;
  display: inline-block;
  width: 220%;
  max-width: 75rem; 
  height: auto;
  overflow: hidden;
  margin-top: -7vw;
  margin-left: -5vw;
}

.frame-image {
  display: block; /* Prevents bottom space under image */
  width: 100%;
  height: auto;
  position: relative; /* Ensure it's part of the layout flow */
  z-index: 2; 
}

.content-window {
  position: absolute; /* Positioned relative to frame-container */
  overflow: hidden; /* Crucial: This clips the content image */
  z-index: 1; /* Behind the frame image if frame has transparency */
  /* top, left, width, height are set by computed style 'contentWindowStyle' */
}

.content-image {
  display: block;
  transition: transform 0.1s linear; /* Smooth transition for scrolling */
  /* width, height, transform are set by computed style 'contentImageStyle' */
}

@media (max-width: 2000px) {
  .frame-container {
    max-width: 65vw;
    margin-top: -8vw;
  }
}
@media (max-width: 600px) {
  .frame-container {
    max-width: 10vw;
    min-width: 0;
  }
}

.fade-enter-active, .fade-leave-active {
  transition: opacity 0.5s;
}
.fade-enter-from, .fade-leave-to {
  opacity: 0;
}
.fading {
  position: absolute;
  top: 0; left: 0; width: 100%; height: auto;
  z-index: 0;
}
</style> 